/* global trapFocus, removeTrapFocus */

/**
 * Home Lovers sign-up overlay (ClickUp 869djwqbd).
 *
 * One global <membership-overlay> (sections/membership-overlay.liquid, in overlay-group)
 * opened by the membership checkbox in the cart drawer and on the cart page. Each checkbox
 * is matched to the overlay by aria-controls === this overlay's id.
 *
 * Checkbox behaviour (confirmed with client):
 *  - click → tick the box + open the overlay
 *  - close the overlay WITHOUT signing up → untick
 *  - sign up (Shopify Form submitted) → stays ticked, including after returning to the cart
 *    (persisted in sessionStorage for the tab + a `home_lovers_signup` cart attribute, which
 *    Liquid reads to pre-tick on a fresh load).
 *
 * Success signalling: both form options converge on the `membership:signed-up` document event.
 * The ILVA Home Lovers form block (home-lovers-form.js) dispatches it itself and is what both
 * storefronts run today; observeFormSuccess() covers an overlay still configured with the
 * Shopify Forms @app block, which exposes no documented "submitted" event and so has to be
 * detected by watching its shadow DOM. assets/datalayer.js listens for the same event to
 * report GA4 `create_account_in_cart` (or `create_account` for the Bliv medlem page's form), so
 * tracking does not depend on this element existing.
 */
if (!customElements.get('membership-overlay')) {
  const SIGNUP_KEY = 'home_lovers_signup';

  // Membership is granted seconds AFTER the form submits: Shopify Forms tags the
  // customer, a Flow reacts to the tag and sets rubiq.is_club_member, and only
  // cart writes AFTER that make the discount Function price the cart as a member.
  // One immediate write always loses that race, so after signup the cart is
  // re-poked on this schedule and re-rendered the moment the Medlemspris
  // allocation actually appears in a poke's response (ClickUp 869ee0any).
  // Logged-out signups never produce an allocation (the Function needs a
  // logged-in customer) — the window just runs out silently.
  const MEMBER_PRICE_RETRY_DELAYS_MS = [5000, 15000, 30000, 60000, 90000];

  class MembershipOverlay extends HTMLElement {
    connectedCallback() {
      this.panel = this.querySelector('.membership-overlay__panel');
      this.formContainer = this.querySelector('[data-membership-form]');
      this.bodyLockedByUs = false;

      this.documentClickHandler = this.handleDocumentClick.bind(this);
      this.documentKeydownHandler = this.handleDocumentKeydown.bind(this);
      this.closeClickHandler = this.handleCloseClick.bind(this);
      this.consentLinkClickHandler = this.handleConsentLinkClick.bind(this);
      this.keyupHandler = this.handleKeyup.bind(this);
      this.signedUpHandler = this.markSignedUp.bind(this);

      document.addEventListener('click', this.documentClickHandler);
      document.addEventListener('keydown', this.documentKeydownHandler);
      this.addEventListener('click', this.closeClickHandler);
      this.addEventListener('click', this.consentLinkClickHandler);
      // Guaranteed fallback hook: anything can mark signup complete.
      document.addEventListener('membership:signed-up', this.signedUpHandler);

      // Sync signup state: if any trigger is pre-checked by Liquid (from cart attribute),
      // remember it in sessionStorage so hasSignedUp() works correctly. If none are checked,
      // clear sessionStorage to avoid state mismatch when the cart attribute is missing.
      const anyChecked = this.triggerCheckboxes.some(cb => cb.checked);
      try {
        if (anyChecked) {
          sessionStorage.setItem(SIGNUP_KEY, 'true');
        } else if (!this.hasSignedUp()) {
          sessionStorage.removeItem(SIGNUP_KEY);
        }
      } catch (error) {}

      // Reflect a prior sign-up (this session) onto the checkbox(es).
      if (this.hasSignedUp()) this.setTriggers(true);

      this.observeFormSuccess();
      this.observeCheckboxReplacements();
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.documentClickHandler);
      document.removeEventListener('keydown', this.documentKeydownHandler);
      document.removeEventListener('membership:signed-up', this.signedUpHandler);
      this.removeEventListener('click', this.closeClickHandler);
      this.removeEventListener('click', this.consentLinkClickHandler);
      this.removeEventListener('keyup', this.keyupHandler);
      if (this.observer) this.observer.disconnect();
      if (this.checkboxObserver) this.checkboxObserver.disconnect();
      if (this.formPoll) clearInterval(this.formPoll);
      this.clearMemberPriceRetries();
      if (this.bodyLockedByUs) document.body.classList.remove('overflow-hidden');
    }

    /* ---------- trigger checkboxes ---------- */

    get triggerCheckboxes() {
      return Array.from(document.querySelectorAll(`input[type="checkbox"][aria-controls="${this.id}"]`));
    }

    /**
     * Sets the checked state of every membership checkbox that controls this overlay.
     * @param {boolean} checked - Whether the boxes should be ticked.
     */
    setTriggers(checked) {
      this.triggerCheckboxes.forEach((checkbox) => { checkbox.checked = checked; });
    }

    /**
     * Returns the membership trigger row this overlay controls from an event, else null.
     * @param {object} evt - Event object.
     * @returns {Element|null}
     */
    triggerFromEvent(evt) {
      const trigger = evt.target.closest('[data-membership-trigger]');
      if (!trigger) return null;
      const checkbox = trigger.querySelector(`input[aria-controls="${this.id}"]`);
      return checkbox ? trigger : null;
    }

    /* ---------- signed-up state ---------- */

    hasSignedUp() {
      try {
        return sessionStorage.getItem(SIGNUP_KEY) === 'true';
      } catch (error) {
        return false;
      }
    }

    /**
     * Marks the customer as signed up: keep the box ticked, remember it for the tab, and
     * persist a cart attribute so the box renders ticked on the next cart load.
     */
    markSignedUp() {
      // GA4 reporting is NOT done here: assets/datalayer.js listens for
      // `membership:signed-up` directly, so the create_account_in_cart event no longer
      // depends on this element being in the DOM, nor on hasSignedUp() - which
      // is seeded from the home_lovers_signup cart attribute and would suppress
      // the event for the life of that cart. This method owns cart state only.
      try {
        sessionStorage.setItem(SIGNUP_KEY, 'true');
      } catch (error) {
        /* sessionStorage unavailable — the cart attribute below still persists it */
      }
      this.setTriggers(true);
      if (this.observer) this.observer.disconnect();
      if (this.formPoll) { clearInterval(this.formPoll); this.formPoll = null; }

      const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
      this.refreshPending = true;
      fetch(`${root}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: { [SIGNUP_KEY]: 'true' } })
      })
        // Every cart write makes Shopify re-run the discount Functions, and the
        // update.js response carries the recomputed cart — so it tells us
        // directly whether the Medlemspris allocation has landed. It almost
        // never has on this first write (the tag→Flow→metafield grant takes a
        // few seconds), so on a miss the retry schedule takes over instead of
        // re-rendering an unchanged cart.
        .then((response) => (response.ok ? response.json() : null))
        .then((cart) => {
          if (cart && MembershipOverlay.hasMemberAllocation(cart)) {
            this.refreshCart();
          } else {
            this.scheduleMemberPriceRetries();
          }
        })
        .catch(() => { this.scheduleMemberPriceRetries(); })
        .finally(() => { this.refreshPending = false; });
    }

    /**
     * Returns whether any cart line carries the member-price discount. Matches
     * the allocation title the same way cart-items.liquid does (the backend
     * Function's discount is titled "Medlemspris"; the English default label
     * is accepted too in case the discount is ever renamed to match locales).
     * @param {object} cart - Cart JSON as returned by the AJAX cart API.
     * @returns {boolean}
     */
    static hasMemberAllocation(cart) {
      return (cart.items || []).some((item) => (item.line_level_discount_allocations || []).some((allocation) => {
        const title = ((allocation.discount_application && allocation.discount_application.title) || '').toLowerCase();
        return title === 'medlemspris' || title === 'member price';
      }));
    }

    /**
     * Schedules the post-signup re-pokes. Timers deliberately survive the
     * overlay closing ("Tilbage til kurv") — the customer is back at the cart
     * exactly when the price should flip. Cleared only on element disconnect
     * or once a poke detects the discount.
     */
    scheduleMemberPriceRetries() {
      if (this.memberPriceRetryTimers && this.memberPriceRetryTimers.length) return; // already scheduled
      this.memberPriceRetryTimers = MEMBER_PRICE_RETRY_DELAYS_MS.map((delay) => setTimeout(() => {
        this.pokeCartForMemberPrice();
      }, delay));
    }

    clearMemberPriceRetries() {
      (this.memberPriceRetryTimers || []).forEach((timer) => clearTimeout(timer));
      this.memberPriceRetryTimers = null;
    }

    /**
     * One retry poke: a storage-neutral attribute write (clearing a
     * non-existent attribute stores nothing but still counts as a cart write,
     * which re-runs the discount Functions — same trick as
     * member-price-sync.js). Re-renders and stops the schedule as soon as the
     * response carries the member allocation; a failed poke just lets the
     * remaining timers try again.
     */
    pokeCartForMemberPrice() {
      const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
      this.refreshPending = true;
      fetch(`${root}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: { member_price_sync: '' } })
      })
        .then((response) => (response.ok ? response.json() : null))
        .then((cart) => {
          if (cart && MembershipOverlay.hasMemberAllocation(cart)) {
            this.clearMemberPriceRetries();
            this.refreshCart();
          }
        })
        .catch(() => {})
        .finally(() => { this.refreshPending = false; });
    }

    /**
     * Re-renders the cart page items or the cart drawer so the visible prices
     * match the server cart (same pattern as discount-code.js). Never opens
     * the drawer — the overlay is usually on top of it.
     */
    refreshCart() {
      const mainCartItems = Array.from(document.querySelectorAll('cart-items'))
        .find((el) => !el.closest('cart-drawer'));
      if (mainCartItems && typeof mainCartItems.refresh === 'function') {
        mainCartItems.refresh();
        return;
      }
      if (document.querySelector('cart-drawer')) {
        document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh'));
      }
    }

    /* ---------- open / close ---------- */

    handleDocumentClick(evt) {
      const trigger = this.triggerFromEvent(evt);
      if (!trigger) return;
      // Clicking the box/label opens the overlay and shows it ticked.
      this.setTriggers(true);
      this.open(trigger);
    }

    handleDocumentKeydown(evt) {
      if (evt.key !== 'Enter' && evt.key !== ' ') return;
      const trigger = this.triggerFromEvent(evt);
      if (!trigger) return;
      evt.preventDefault();
      this.setTriggers(true);
      this.open(trigger);
    }

    handleCloseClick(evt) {
      if (evt.target.closest('.js-membership-close')) this.close();
    }

    /**
     * The Home Lovers form block's consent checkboxes can link to content
     * further down (e.g. the club terms). A native href="#id" jump scrolls
     * the document, not .membership-overlay__body (the panel's actual
     * scrolling container, sat behind position:fixed/overflow:hidden
     * ancestors) — so this scrolls the target into view manually instead.
     */
    handleConsentLinkClick(evt) {
      const link = evt.target.closest('.home-lovers-form__consent-link');
      if (!link) return;

      const targetId = (link.getAttribute('href') || '').slice(1);
      const target = targetId && this.querySelector(`#${CSS.escape(targetId)}`);
      if (!target) return;

      evt.preventDefault();
      target.scrollIntoView({ block: 'start', behavior: 'smooth' });
    }

    handleKeyup(evt) {
      if (evt.key !== 'Escape') return;
      evt.stopPropagation(); // keep a cart drawer behind us open
      this.close();
    }

    /**
     * Opens the overlay.
     * @param {Element} trigger - The membership trigger row, focused again on close.
     */
    open(trigger) {
      const checkbox = trigger.querySelector('input[type="checkbox"]');
      this.opener = checkbox || trigger;

      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');

      // Lock body scroll only if something else (e.g. the cart drawer) hasn't already,
      // so closing us doesn't unlock a still-open drawer.
      this.bodyLockedByUs = !document.body.classList.contains('overflow-hidden');
      if (this.bodyLockedByUs) document.body.classList.add('overflow-hidden');

      trapFocus(this.panel);
      this.addEventListener('keyup', this.keyupHandler);

      // Shopify Forms only hydrates the form's shadow root once it's visible, so (re)attach
      // the success observer now that the overlay is open — the page-load attempt may have
      // run while the form was still hidden and unhydrated.
      this.observeFormSuccess();
    }

    /**
     * Closes the overlay.
     */
    close() {
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      if (this.bodyLockedByUs) {
        document.body.classList.remove('overflow-hidden');
        this.bodyLockedByUs = false;
      }
      this.removeEventListener('keyup', this.keyupHandler);

      if (this.formPoll) {
        clearInterval(this.formPoll);
        this.formPoll = null;
      }
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }

      // Untick unless they actually signed up.
      if (!this.hasSignedUp()) this.setTriggers(false);

      // Re-arm the cart drawer's focus trap if it's open behind us; else release.
      // The post-signup cart refresh replaces the summary DOM while the overlay
      // is open, detaching the checkbox we opened from — re-resolve it so focus
      // returns to the replacement instead of a dead node.
      const drawer = document.getElementById('cart-drawer');
      const drawerOpen = drawer && drawer.hasAttribute('open');
      if (this.opener && !this.opener.isConnected) {
        this.opener = this.triggerCheckboxes.find((checkbox) => !drawerOpen || drawer.contains(checkbox)) || null;
      }
      const openerToFocus = this.refreshPending ? null : this.opener;
      if (drawerOpen) {
        trapFocus(drawer, openerToFocus);
      } else {
        removeTrapFocus(openerToFocus);
      }
    }

    /* ---------- Shopify Form success detection ----------
       The Shopify Forms inline form renders as <shopify-forms-embed> with an OPEN shadow
       root. Inside it, a stable <div data-current-step="form"> flips to "success" once the
       form is submitted — that's our signal. We observe the shadow root (it hydrates
       asynchronously, so we poll until it exists). A `membership:signed-up` event still
       works as a manual fallback. If the success step turns out to be named differently,
       adjust the value below (check shadowRoot's [data-current-step] after a real submit). */
    observeFormSuccess() {
      if (this.hasSignedUp() || this.observer) return; // already signed up / already watching

      // The ILVA Home Lovers form block signals success itself (it dispatches
      // `membership:signed-up` from home-lovers-form.js), so there is no Shopify
      // Forms embed to watch and nothing to poll for. Both ILVA storefronts run
      // that block today; this watcher only serves an overlay still configured
      // with the Shopify Forms @app block. Without this bail-out the poll below
      // ran its full ~20s on every pageload of the site, always fruitlessly.
      if (this.querySelector('home-lovers-form')) return;

      const succeeded = (root) => {
        const step = root && root.querySelector('[data-current-step]');
        return !!step && step.getAttribute('data-current-step') === 'success';
      };

      // Success is announced, not applied directly, so that both form paths
      // converge on one signal with two independent consumers: this element
      // (cart state) and datalayer.js (GA4).
      const announce = () => document.dispatchEvent(new CustomEvent('membership:signed-up'));

      const attach = () => {
        const host = this.formContainer && this.formContainer.querySelector('shopify-forms-embed');
        const root = host && host.shadowRoot;
        if (!root) return false; // form / shadow root not hydrated yet
        if (succeeded(root)) { announce(); return true; }
        if (this.observer) return true; // observer already attached
        this.observer = new MutationObserver(() => {
          if (succeeded(root)) announce();
        });
        this.observer.observe(root, {
          subtree: true,
          childList: true,
          attributes: true,
          attributeFilter: ['data-current-step']
        });
        return true;
      };

      if (attach()) {
        if (this.formPoll) { clearInterval(this.formPoll); this.formPoll = null; }
        return;
      }
      if (this.formPoll) return; // a poll is already waiting for hydration
      // Shopify Forms hydrates the shadow root lazily (and only once the overlay is visible),
      // so poll until host + shadow root exist, then attach (≈20s window).
      let attempts = 0;
      this.formPoll = setInterval(() => {
        if (attach() || (attempts += 1) > 80) {
          clearInterval(this.formPoll);
          this.formPoll = null;
        }
      }, 250);
    }

    /* ---------- cart refresh detection ----------
       Cart quantity updates replace the summary HTML (including the membership checkbox).
       Watch for new checkboxes and re-apply the signed-up state. */
    observeCheckboxReplacements() {
      // Always watch — sign-up can happen mid-session, after which any cart re-render that
      // swaps in a fresh (unchecked) checkbox must be re-ticked.
      this.checkboxObserver = new MutationObserver(() => {
        if (this.hasSignedUp()) this.setTriggers(true);
      });

      // Watch the cart-drawer element itself (not .cart-drawer__summary) because
      // renderContents replaces innerHTML of cart-drawer, destroying child summary nodes
      // while the cart-drawer element persists. Also watch .cart__summary on the cart page.
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer) {
        this.checkboxObserver.observe(cartDrawer, { childList: true, subtree: true });
      }
      const cartSummary = document.querySelector('.cart__summary');
      if (cartSummary) {
        this.checkboxObserver.observe(cartSummary, { childList: true, subtree: true });
      }
    }
  }

  customElements.define('membership-overlay', MembershipOverlay);
}
